import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useAuth } from "@/hooks/useAuth";

export interface ChatMessage {
  role: "user" | "assistant";
  content: string;
}

interface ChatSession {
  id: string;
  title: string;
  messages: ChatMessage[];
  createdAt: string;
  updatedAt: string;
}

interface ChatContextValue {
  sessions: ChatSession[];
  activeId: string;
  messages: ChatMessage[];
  isStreaming: boolean;
  error: string | null;
  drawerOpen: boolean;
  openDrawer: () => void;
  closeDrawer: () => void;
  newSession: () => void;
  switchSession: (id: string) => void;
  deleteSession: (id: string) => void;
  send: (text: string) => Promise<void>;
}

const ChatContext = createContext<ChatContextValue | null>(null);

function sessionsKey(email: string) { return `felix:sessions:v1:${email}`; }

function createSession(): ChatSession {
  return {
    id: crypto.randomUUID(),
    title: "New chat",
    messages: [],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
}

export function ChatProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [activeId, setActiveId] = useState("");
  const [drawerOpen, setDrawerOpenState] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const active = sessions.find((s) => s.id === activeId);
  const messages = active?.messages ?? [];

  // Load per-user on mount; always start on a fresh empty session.
  useEffect(() => {
    if (!user?.email) return;
    try {
      const raw = localStorage.getItem(sessionsKey(user.email));
      const loaded: ChatSession[] = raw ? JSON.parse(raw) : [];
      const prior = Array.isArray(loaded) ? loaded.filter((s) => s.messages?.length) : [];
      const fresh = createSession();
      setSessions([fresh, ...prior]);
      setActiveId(fresh.id);
    } catch {
      const fresh = createSession();
      setSessions([fresh]);
      setActiveId(fresh.id);
    }
  }, [user?.email]);

  // Persist (cap 30)
  useEffect(() => {
    if (!user?.email || !sessions.length) return;
    try { localStorage.setItem(sessionsKey(user.email), JSON.stringify(sessions.slice(0, 30))); } catch {}
  }, [sessions, user?.email]);

  const updateActive = useCallback((fn: (s: ChatSession) => ChatSession) => {
    setSessions((prev) => prev.map((s) => (s.id === activeId ? fn(s) : s)));
  }, [activeId]);

  const openDrawer = useCallback(() => setDrawerOpenState(true), []);
  const closeDrawer = useCallback(() => setDrawerOpenState(false), []);

  const newSession = useCallback(() => {
    abortRef.current?.abort();
    const fresh = createSession();
    setSessions((prev) => [fresh, ...prev.filter((s) => s.messages.length > 0)]);
    setActiveId(fresh.id);
    setError(null);
  }, []);

  const switchSession = useCallback((id: string) => {
    abortRef.current?.abort();
    setActiveId(id);
    setError(null);
  }, []);

  const deleteSession = useCallback((id: string) => {
    setSessions((prev) => {
      const next = prev.filter((s) => s.id !== id);
      if (next.length === 0) {
        const fresh = createSession();
        setActiveId(fresh.id);
        return [fresh];
      }
      if (id === activeId) setActiveId(next[0].id);
      return next;
    });
  }, [activeId]);

  const send = useCallback(async (text: string) => {
    if (!text.trim() || isStreaming) return;
    const userMsg: ChatMessage = { role: "user", content: text.trim() };
    const isFirst = messages.length === 0;
    const next = [...messages, userMsg];

    updateActive((s) => ({
      ...s,
      messages: next,
      title: isFirst ? text.trim().slice(0, 50) : s.title,
      updatedAt: new Date().toISOString(),
    }));

    // As soon as user sends, ensure drawer is open.
    setDrawerOpenState(true);

    setError(null);
    setIsStreaming(true);
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    try {
      const res = await fetch("/api/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ messages: next }),
        credentials: "include",
        signal: ctrl.signal,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: "AI unavailable" }));
        throw new Error(err.error ?? "Request failed");
      }

      const reader = res.body!.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let assistantText = "";

      updateActive((s) => ({
        ...s,
        messages: [...next, { role: "assistant", content: "" }],
        updatedAt: new Date().toISOString(),
      }));

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() ?? "";
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const payload = line.slice(6).trim();
          if (payload === "[DONE]") continue;
          try {
            const chunk = JSON.parse(payload);
            const delta = chunk.choices?.[0]?.delta?.content ?? "";
            if (delta) {
              assistantText += delta;
              updateActive((s) => ({
                ...s,
                messages: [...s.messages.slice(0, -1), { role: "assistant", content: assistantText }],
                updatedAt: new Date().toISOString(),
              }));
            }
          } catch {}
        }
      }
    } catch (err: unknown) {
      if (err instanceof Error && err.name === "AbortError") return;
      updateActive((s) => ({
        ...s,
        messages:
          s.messages[s.messages.length - 1]?.role === "assistant" && !s.messages[s.messages.length - 1].content
            ? s.messages.slice(0, -1)
            : s.messages,
      }));
      setError(err instanceof Error ? err.message : "Something went wrong");
    } finally {
      setIsStreaming(false);
    }
  }, [messages, isStreaming, updateActive]);

  const value: ChatContextValue = {
    sessions, activeId, messages, isStreaming, error, drawerOpen,
    openDrawer, closeDrawer, newSession, switchSession, deleteSession, send,
  };

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
}

export function useChat() {
  const ctx = useContext(ChatContext);
  if (!ctx) throw new Error("useChat must be used inside ChatProvider");
  return ctx;
}
